import katex from "katex";
import { Sigma, Beaker, Clock, PenTool, BookOpen } from "lucide-react";
import { latexToUnicode } from "@/lib/latex-unicode";

/** Normalise LLM formula strings into a KaTeX-friendly LaTeX string.
 * Handles both new LaTeX-native output and legacy `x^2 / H2O` shorthand. */
export function toLatex(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  s = s.replace(/^\$+|\$+$/g, "");
  const looksLikeLatex = /\\(frac|cdot|times|Delta|log|ln|sqrt|pi|rightarrow|leftarrow|pm|mp|leq|geq)|[\^_]\{/.test(s);
  if (!looksLikeLatex) {
    s = s.replace(/([A-Za-z0-9\]\)])\^([+\-]?\d+|[A-Za-z](?![A-Za-z]))/g, "$1^{$2}");
    s = s.replace(/([A-Za-z])_([A-Za-z0-9](?![A-Za-z0-9]))/g, "$1_{$2}");
    s = s.replace(/([A-Z][a-z]?)(\d+)/g, "$1_{$2}");
    s = s.replace(/·|•/g, "\\cdot ").replace(/×/g, "\\times ");
    s = s.replace(/->|→/g, "\\rightarrow ");
  }
  return s;
}

export function LatexFormula({ code, block = true }) {
  try {
    const html = katex.renderToString(toLatex(code), {
      throwOnError: false,
      displayMode: block,
      output: "html",
      strict: "ignore",
    });
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return <span className="font-mono">{code}</span>;
  }
}

export const SUBJECT_META = {
  math: { icon: Sigma, label: "Mathematics" },
  physics: { icon: Sigma, label: "Physics" },
  biology: { icon: Beaker, label: "Biology" },
  chemistry: { icon: Beaker, label: "Chemistry" },
  social_science: { icon: Clock, label: "Social Science" },
  english: { icon: PenTool, label: "English / Literature" },
  general: { icon: BookOpen, label: "General" },
};

/* keyword highlighter */
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function buildKeywordRegex(keywords) {
  if (!keywords?.length) return null;
  const cleaned = keywords
    .map((k) => (typeof k === "string" ? k.trim() : ""))
    .filter((k) => k && k.length >= 2)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegex);
  if (!cleaned.length) return null;
  return new RegExp(`(${cleaned.join("|")})`, "gi");
}

export function highlight(text, regex) {
  if (!text) return text;
  const str = latexToUnicode(String(text));
  if (!regex) return str;
  const parts = [];
  let last = 0;
  regex.lastIndex = 0;
  let m;
  while ((m = regex.exec(str)) !== null) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    parts.push(
      <mark
        key={`${m.index}-${m[0]}`}
        className="bg-[#DBEAFE] text-black px-1 py-0.5 rounded-sm font-semibold decoration-0"
      >
        {m[0]}
      </mark>
    );
    last = m.index + m[0].length;
    if (m.index === regex.lastIndex) regex.lastIndex++;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts;
}

/**
 * Split a string on inline LaTeX delimiters and render each math segment via
 * KaTeX. Non-math text still runs through `highlight()` so keyword matches and
 * latex-unicode conversion still apply.
 *
 * Handles: \( ... \)   \[ ... \]   $$ ... $$   $ ... $
 */
const INLINE_MATH_RE = /\\\(([\s\S]+?)\\\)|\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|(?<!\\)\$([^$\n]+?)\$/g;

export function renderRich(text, regex) {
  if (!text) return text;
  const str = String(text);
  if (!INLINE_MATH_RE.test(str)) {
    INLINE_MATH_RE.lastIndex = 0;
    return highlight(str, regex);
  }
  INLINE_MATH_RE.lastIndex = 0;
  const nodes = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = INLINE_MATH_RE.exec(str)) !== null) {
    if (m.index > last) {
      nodes.push(<span key={`t-${k++}`}>{highlight(str.slice(last, m.index), regex)}</span>);
    }
    const latex = (m[1] || m[2] || m[3] || m[4] || "").trim();
    const display = !!(m[2] || m[3]); // \[...\] and $$...$$ are display mode
    try {
      const html = katex.renderToString(latex, {
        throwOnError: false,
        displayMode: display,
        output: "html",
        strict: "ignore",
      });
      nodes.push(
        <span
          key={`m-${k++}`}
          className={display ? "block my-2" : "inline-block align-middle"}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    } catch {
      nodes.push(<span key={`m-${k++}`} className="font-mono">{latex}</span>);
    }
    last = m.index + m[0].length;
    if (m.index === INLINE_MATH_RE.lastIndex) INLINE_MATH_RE.lastIndex++;
  }
  if (last < str.length) {
    nodes.push(<span key={`t-${k++}`}>{highlight(str.slice(last), regex)}</span>);
  }
  return nodes;
}

/* shared section wrapper */
export function Section({ label, children, dense = false }) {
  return (
    <section className={dense ? "mt-6" : "mt-8"}>
      <div className="flex items-center gap-2 mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3B82F6]">/ {label}</p>
        <span className="h-[3px] flex-1 max-w-[80px] bg-[#BFDBFE]" aria-hidden />
      </div>
      {children}
    </section>
  );
}

export function Tags({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-1.5">
      {tags.map((t, i) => (
        <span
          key={i}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-black text-[#DBEAFE]"
        >
          #{t}
        </span>
      ))}
    </div>
  );
}
